/**
 * eprScoreEngine.ts
 *
 * Pure scoring functions for the HR Performance Review Framework (hr_v1).
 * No Firestore reads/writes. All inputs are passed in; all outputs are
 * derived. Independently testable against the tables in CLAUDE.md §C.
 *
 * Implements:
 *   - §C.2 — item final score = average(self, manager); weighted total
 *   - §C.7 — evidence required when score = 5 or ≤ 2
 *
 * Does NOT implement calibration (§E) — that lives in a separate engine.
 */

import type {
  EPRItem, EPRSubmission, EPRWeights,
  EvidenceTriggerConfig, EPRScaleConfig, ScaleDef, Score1to5,
} from '../types/eprFramework';
import { DEFAULT_SCALE_CONFIG } from '../types/eprFramework';

// ─── Item-level helpers ─────────────────────────────────────────────────────

/**
 * Item final score per §C.2: average(employeeSelfScore, managerScore).
 * Returns undefined if either side is missing — the average is undefined
 * until both sides have submitted.
 */
export function computeItemFinalScore(item: EPRItem): number | undefined {
  const self = item.employeeSelfScore;
  const mgr  = item.managerScore;
  if (self == null || mgr == null) return undefined;
  return (self + mgr) / 2;
}

/**
 * Average across all items in a component.
 *
 * Uses each item's itemFinalScore when both sides are submitted. Items with
 * one side missing are EXCLUDED from the average — never coerced to zero.
 * Returns undefined if no item has a defined final score (no useful average).
 */
export function computeComponentScore(items: EPRItem[]): number | undefined {
  const finals = items
    .map(it => it.itemFinalScore ?? computeItemFinalScore(it))
    .filter((x): x is number => typeof x === 'number');
  if (finals.length === 0) return undefined;
  const sum = finals.reduce((a, b) => a + b, 0);
  return sum / finals.length;
}

// ─── Weighted total (§C.2) ──────────────────────────────────────────────────

export interface RawComponentScores {
  rawOkr?: number;
  rawKpi?: number;
  rawCore?: number;
  rawLeadership?: number;
}

/**
 * Weighted EPR total. Sums (componentScore * componentWeight) for all
 * components that have a score. Components with weight 0 are skipped
 * (e.g. Leadership for non-people-managers — CLAUDE.md §J #3).
 *
 * Returns undefined if any required component (weight > 0) lacks a score.
 */
export function computeRawTotal(
  scores: RawComponentScores,
  weights: EPRWeights,
): number | undefined {
  const parts: Array<{ score: number | undefined; weight: number; key: string }> = [
    { score: scores.rawOkr,        weight: weights.okr,        key: 'okr' },
    { score: scores.rawKpi,        weight: weights.kpi,        key: 'kpi' },
    { score: scores.rawCore,       weight: weights.core,       key: 'core' },
    { score: scores.rawLeadership, weight: weights.leadership, key: 'leadership' },
  ];
  let total = 0;
  for (const p of parts) {
    if (p.weight <= 0) continue;             // skip zero-weighted (e.g. non-mgr leadership)
    if (typeof p.score !== 'number') return undefined;  // required but missing
    total += p.score * p.weight;
  }
  return total;
}

/**
 * Compute all raw component scores plus total from a fully-loaded
 * EPRSubmission. Use this once both employee and manager have submitted.
 */
export function computeSubmissionScores(sub: EPRSubmission): {
  rawOkr?: number;
  rawKpi?: number;
  rawCore?: number;
  rawLeadership?: number;
  rawTotal?: number;
} {
  const rawOkr        = computeComponentScore(sub.okrItems);
  const rawKpi        = computeComponentScore(sub.kpiItems);
  const rawCore       = computeComponentScore(sub.coreItems);
  const rawLeadership = sub.weights.leadership > 0
    ? computeComponentScore(sub.leadershipItems)
    : undefined;
  const rawTotal = computeRawTotal(
    { rawOkr, rawKpi, rawCore, rawLeadership },
    sub.weights,
  );
  return { rawOkr, rawKpi, rawCore, rawLeadership, rawTotal };
}

// ─── Scale helpers (template-driven) ────────────────────────────────────────

/** Integer within the submission's scale snapshot ([min, max] inclusive). */
export function isValidScore(score: number, scale: EPRScaleConfig = DEFAULT_SCALE_CONFIG): boolean {
  return Number.isInteger(score) && score >= scale.min && score <= scale.max;
}

/**
 * Map an achievement % to its band score per the §C.3 / §C.4 tables (or a
 * template's own bands). Used to auto-suggest the score once the employee
 * has entered Target + Actual — the scorer can still override.
 *
 * A band without achievementMin is open-ended downward; without
 * achievementMax, open-ended upward. Returns undefined when no band
 * matches (mis-configured template) — never guesses.
 */
export function suggestScoreFromAchievement(
  achievementPct: number,
  scale: ScaleDef,
): number | undefined {
  if (!Number.isFinite(achievementPct) || achievementPct < 0) return undefined;
  for (const band of scale.bands) {
    const minOk = band.achievementMin == null || achievementPct >= band.achievementMin;
    const maxOk = band.achievementMax == null || achievementPct <= band.achievementMax;
    if (minOk && maxOk) return band.score;
  }
  return undefined;
}

/** Achievement % from numeric target/actual strings. Undefined when not computable. */
export function computeAchievementPct(target?: string, actual?: string): number | undefined {
  const t = parseFloat((target ?? '').replace(/,/g, ''));
  const a = parseFloat((actual ?? '').replace(/,/g, ''));
  if (!Number.isFinite(t) || !Number.isFinite(a) || t === 0) return undefined;
  return Math.round((a / t) * 1000) / 10;  // one decimal place
}

// ─── Evidence gating (§C.7) ─────────────────────────────────────────────────

/**
 * Does this item require a non-empty `notesEvidence` from the given scorer?
 * Per §C.7 (default scale): score = 5 or ≤ 2 triggers the requirement. On a
 * template scale the trigger comes from scaleConfig.evidenceTrigger. The
 * check is made against the SCORER's own column (employee checks
 * self-score; manager checks manager-score).
 */
export function isEvidenceRequiredForScorer(
  item: EPRItem,
  scorer: 'employee' | 'manager',
  trigger: EvidenceTriggerConfig = DEFAULT_SCALE_CONFIG.evidenceTrigger,
): boolean {
  const score = scorer === 'employee' ? item.employeeSelfScore : item.managerScore;
  if (score == null) return false;
  return scoreTriggersEvidence(score, trigger);
}

export function scoreTriggersEvidence(
  score: number,
  trigger: EvidenceTriggerConfig = DEFAULT_SCALE_CONFIG.evidenceTrigger,
): boolean {
  return score >= trigger.highMin || score <= trigger.lowMax;
}

/**
 * Narrow a stored score (EPRItem fields are plain `number` so Firestore
 * reads type-check) back to the Score1to5 domain. Lives here, not in
 * `types/`, because types/ is runtime-free.
 */
export function isScore1to5(score: number): score is Score1to5 {
  return Number.isInteger(score) && score >= 1 && score <= 5;
}

// ─── Validation issues ──────────────────────────────────────────────────────

export interface ValidationIssue {
  itemId?: string;
  component?: 'okr' | 'kpi' | 'core' | 'leadership';
  field: 'employeeSelfScore' | 'managerScore' | 'notesEvidence' | 'component';
  code:
    | 'missing_score'
    | 'missing_evidence'
    | 'empty_component'
    | 'invalid_value';
  message: string;
}

/**
 * Validate a single item for a scorer's submit. Returns the list of issues
 * (empty list = clean).
 */
export function validateItemForSubmit(
  item: EPRItem,
  scorer: 'employee' | 'manager',
  scale: EPRScaleConfig = DEFAULT_SCALE_CONFIG,
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const score = scorer === 'employee' ? item.employeeSelfScore : item.managerScore;
  const field = scorer === 'employee' ? 'employeeSelfScore' : 'managerScore';

  if (score == null) {
    issues.push({
      itemId: item.itemId,
      component: item.component,
      field,
      code: 'missing_score',
      message: `Score required for "${item.itemName}".`,
    });
    return issues;
  }

  if (!isValidScore(score, scale)) {
    issues.push({
      itemId: item.itemId,
      component: item.component,
      field,
      code: 'invalid_value',
      message: `Score for "${item.itemName}" must be a whole number between ${scale.min} and ${scale.max}.`,
    });
    return issues;
  }

  if (scoreTriggersEvidence(score, scale.evidenceTrigger) && !(item.notesEvidence?.trim())) {
    issues.push({
      itemId: item.itemId,
      component: item.component,
      field: 'notesEvidence',
      code: 'missing_evidence',
      message: `Evidence required for "${item.itemName}" (score ${score}).`,
    });
  }

  return issues;
}

/**
 * Validate a full submission for a scorer's submit. Walks all relevant
 * components per the submission's weights (Leadership skipped when its
 * weight is 0). Returns a flat list of issues.
 */
export function validateSubmissionForSubmit(
  sub: EPRSubmission,
  scorer: 'employee' | 'manager',
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const scale = sub.templateSnapshot?.scaleConfig ?? DEFAULT_SCALE_CONFIG;

  const componentBuckets: Array<{
    key: 'okr' | 'kpi' | 'core' | 'leadership';
    items: EPRItem[];
    weight: number;
  }> = [
    { key: 'okr',        items: sub.okrItems,        weight: sub.weights.okr },
    { key: 'kpi',        items: sub.kpiItems,        weight: sub.weights.kpi },
    { key: 'core',       items: sub.coreItems,       weight: sub.weights.core },
    { key: 'leadership', items: sub.leadershipItems, weight: sub.weights.leadership },
  ];

  for (const bucket of componentBuckets) {
    if (bucket.weight <= 0) continue;
    if (bucket.items.length === 0) {
      issues.push({
        component: bucket.key,
        field: 'component',
        code: 'empty_component',
        message: `Component "${bucket.key}" must have at least one item.`,
      });
      continue;
    }
    for (const item of bucket.items) {
      issues.push(...validateItemForSubmit(item, scorer, scale));
    }
  }

  return issues;
}
